import { z } from 'zod';
import { AgentStatusSchema, AgentTypeSchema, AxonEventSchema } from '@reflex/shared';

/**
 * Slot id of the Replays page tab strip. This plugin owns the page at
 * `/replays` and renders the Agents tab; any plugin that has its own kind of
 * replay adds a tab with a `pageTabs` manifest entry for this slot instead of
 * shipping a sibling page users have to navigate between (`plugin-flows`
 * contributes the Automations tab this way). Lives in `shared` so
 * contributors reference the slot by constant rather than a copied string.
 */
export const REPLAYS_PAGE_TAB_SLOT = 'replays';

/**
 * Snapshot of an agent run that can drive the replay viewer. Stored inline
 * inside a {@link SavedReplay.data} column and also the shape exported to
 * JSON files so marketing embeds can load a replay via `fetch(src)` without
 * going through the REST API.
 */
export const ReplayAgentSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  agentType: AgentTypeSchema,
  status: AgentStatusSchema,
  streamId: z.string(),
  model: z.string().nullable().optional(),
  prompt: z.string().nullable().optional(),
  createdAt: z.number(),
});
export type ReplayAgentSnapshot = z.infer<typeof ReplayAgentSnapshotSchema>;

/**
 * Timeline annotation: a popup that explains what's happening at a
 * specific point in the replay. Anchored to an event by index, with
 * `position` controlling whether the popup appears before that event
 * is revealed or after it has rendered.
 *
 * Behavior in the engine:
 * - Interactive mode: playback pauses at the annotation until the
 *   viewer clicks "Okay" (`dismissAnnotation()`).
 * - Non-interactive mode: the popup auto-dismisses after
 *   `autoDismissMs` (engine default 5000ms when omitted).
 */
export const ReplayAnnotationSchema = z.object({
  id: z.string(),
  attachToEventIndex: z.number().int().min(0),
  position: z.enum(['before', 'after']).default('after'),
  title: z.string().optional(),
  body: z.string().min(1),
  autoDismissMs: z.number().int().positive().optional(),
});
export type ReplayAnnotation = z.infer<typeof ReplayAnnotationSchema>;

/**
 * Optional pre-start banner shown before any events render. Gates
 * playback until dismissed: in interactive playback the viewer clicks
 * "Start replay"; in non-interactive playback the banner auto-dismisses
 * after `autoDismissMs` (engine default 5000ms when omitted) and the
 * engine begins revealing events as if it had just been told to play.
 *
 * Lives on {@link ReplayDataSchema} so it travels with the JSON
 * payload that marketing embeds load via `fetch(src)` — the
 * `SavedReplay.name`/`description` fields stay as list-view metadata
 * and are unrelated.
 */
export const ReplayIntroSchema = z.object({
  title: z.string().min(1).optional(),
  body: z.string().min(1),
  autoDismissMs: z.number().int().positive().optional(),
});
export type ReplayIntro = z.infer<typeof ReplayIntroSchema>;

export const ReplayDataSchema = z.object({
  version: z.literal(1),
  agent: ReplayAgentSnapshotSchema,
  events: z.array(AxonEventSchema),
  annotations: z.array(ReplayAnnotationSchema).optional(),
  /**
   * Optional pre-start banner. See {@link ReplayIntroSchema}.
   */
  intro: ReplayIntroSchema.optional(),
  /**
   * Optional list of fake agents shown in the `agents-list` frame's
   * sidebar so the replay can be presented as if it were running inside
   * the workspace. Re-uses {@link ReplayAgentSnapshotSchema} so the JSON
   * shape matches the primary `agent` snapshot. When omitted, the frame
   * falls back to a built-in `DEFAULT_FRAME_AGENTS` set.
   */
  frameAgents: z.array(ReplayAgentSnapshotSchema).optional(),
});
export type ReplayData = z.infer<typeof ReplayDataSchema>;

export const SavedReplaySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  data: ReplayDataSchema,
});
export type SavedReplay = z.infer<typeof SavedReplaySchema>;

export const CreateReplaySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  data: ReplayDataSchema,
});
export type CreateReplay = z.infer<typeof CreateReplaySchema>;

export const UpdateReplaySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  data: ReplayDataSchema.optional(),
});
export type UpdateReplay = z.infer<typeof UpdateReplaySchema>;

export const CreateReplayFromAgentSchema = z.object({
  agentId: z.string().min(1, 'agentId is required'),
  name: z.string().optional(),
  description: z.string().optional(),
});
export type CreateReplayFromAgent = z.infer<typeof CreateReplayFromAgentSchema>;
